import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { SdShowMoreComponent } from '@seb/web-design-system/show-more';
import { IReview, SdReviewComponent } from '@seb/web-design-system/reviews';
import { DeviceService, IComponentWrapper, SdComponentWrapperComponent, SizesEnum } from '@seb/web-design-system/core';
import {
  DcpListResponse,
  DcpResponsePagination,
  Recipe,
  RecipeComment,
  RecipeDetailsPageConfig,
  WC_CONFIG_PROVIDER, WcConfig
} from '@seb/web-common/core/models';
import { HeadingParagraphMapper } from '@seb/web-common/core/mappers';
import { RecipeService, TrackingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-recipe-reviews',
  templateUrl: './recipe-reviews.component.html',
  standalone: true,
  imports: [SdComponentWrapperComponent, SdShowMoreComponent, SdReviewComponent]
})
export class RecipeReviewsComponent implements OnInit, OnDestroy {
  public pagination: DcpResponsePagination = {
    size: 9,
    totalElements: 0,
    totalPages: 0,
    number: -1
  };

  public isMobile$: boolean;
  public isDevice$: boolean;
  public reviews: IReview[] = [];

  public content: IComponentWrapper;
  public reviewsConfig: RecipeDetailsPageConfig;

  showMoreSize = SizesEnum.s;
  paginationSize = 9;
  dynamicContentShowMore = {
    isActive: true,
    isLast: false
  };

  snippetsRecipeReview;

  @Input() class = '';

  private pRecipe: Recipe;
  private readonly destroy$: Subject<void> = new Subject();
  private readonly searchSubject$: Subject<void> = new Subject();
  private mobileSubscription: Subscription;
  private deviceSubscription: Subscription;

  @Input()
  set recipe(value: Recipe) {
    this.pRecipe = value;
    // Get reviews only if necessary
    if (value.communityTopRecipe?.comments) {
      this.initSearch();
    }
  }

  get recipe(): Recipe {
    return this.pRecipe;
  }

  @Output() hasLoaded = new EventEmitter<void>();

  get showSize(): number {
    return this.isMobile$ ? 3 : 1;
  }

  get rowIndexInit(): number {
    return this.isMobile$ ? 3 : 1;
  }

  constructor(
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    private recipeService: RecipeService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private headingParagraphMapper: HeadingParagraphMapper,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig,
    @Inject(PLATFORM_ID) protected platformId: any
  ) {}

  public loadMore(): void {
    this.trackingService.sendEvent('clickMoreReviews', {
      productName: this.pRecipe.title,
      productID: this.pRecipe.fid?.functionalId
    });
    this.searchSubject$.next();
  }

  public initSearch(): void {
    if (this.isDevice$ || this.isMobile$ || (!this.isDevice$ && ! this.isMobile$)) {
      this.reviews.length = 0;
      this.pagination = {
        size: this.paginationSize,
        totalElements: 0,
        totalPages: 0,
        number: -1
      };
      this.paginationSize = this.isDevice$ ? 2 : this.isMobile$ ? 1 : 3; // because showMoreSize S
      this.searchSubject$.next();
    }
  }

  ngOnInit(): void {
    this.reviewsConfig = this.wcConfig?.recipeDetailsPage;

    this.content = this.toWrapper();
    const _paginationSize = this.pagination.number === -1 ? this.paginationSize * 2 : this.paginationSize;

    this.searchSubject$
      .pipe(
        concatMap(() =>
          this.recipeService.getRecipeComments(
            this.recipe.fid.sourceSystem,
            this.recipe.fid.functionalId,
            this.pagination.number + 1,
            _paginationSize
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((commentsRsp: DcpListResponse<RecipeComment>) => {
        if (commentsRsp?.page?.totalElements != null && commentsRsp?.content?.length) {
          this.pagination = commentsRsp.page;
          this.reviews = [
            ...this.reviews,
            ...commentsRsp.content.map((comment: RecipeComment) =>
              this.recipeService.extractReview(this.recipe.title, comment)
            )
          ];
          this.setRichSnippets(commentsRsp?.content);
          if (this.reviews.length >= this.pagination.totalElements) {
            this.dynamicContentShowMore.isLast = true;
          }
          this.hasLoaded.emit();
          this.cdRef.markForCheck();
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      });
      this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
        this.initSearch();
      });
    }
  }

  ngOnDestroy(): void {
    this.mobileSubscription?.unsubscribe();
    this.deviceSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toWrapper(): IComponentWrapper {
    const richParagraphData = {
      heading: {
        headline: this.translateService.instant('recipes.comments.title')
      },
      titleTheme: this.reviewsConfig?.reviews?.titleTheme,
      isDescription: false,
      size: SizesEnum.xl,
      titleLevel: 2
    };
    return {
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData),
      footNotes: [],
      links: []
    };
  }

  setRichSnippets(comments) {
    this.snippetsRecipeReview = [];
    comments.forEach((comment) => {
      this.snippetsRecipeReview.push(
        {
          '@type': 'Review',
          author: comment?.creator?.nickname,
          datePublished: this.formatCreationDate(comment?.creationDate),
          reviewBody: comment?.content,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: comment?.evaluation?.rating
          }
        }
      );
    });
  }

  formatCreationDate(date) {
    const commentDate = new Date(date);
    return commentDate.getFullYear().toString()
      + '-' + ('0' + (commentDate.getMonth() + 1)).slice(-2)
      + '-' + ('0' + commentDate.getDate()).slice(-2);
  }
}
